import { useState, useCallback } from 'react';
import { useWords } from '../../hooks/useWords';
import { getApiErrorMessage } from '../../services/api';
import { Upload, FileText, Loader2, Check, X, AlertCircle } from 'lucide-react';

interface WordUploaderProps {
  topicId: number;
  onSuccess: () => void;
}

interface PreviewWord {
  turkish: string;
  russian?: string;
}

export function WordUploader({ topicId, onSuccess }: WordUploaderProps) {
  const { parsePaste, parseUpload, addWordsBulk } = useWords(topicId);
  const [pasteText, setPasteText] = useState('');
  const [preview, setPreview] = useState<PreviewWord[] | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePaste = async () => {
    if (!pasteText.trim()) return;
    setError(null);
    try {
      const parsed = await parsePaste(pasteText);
      setPreview(parsed);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Не удалось разобрать список'));
    }
  };

  const handleFile = useCallback(
    async (file: File) => {
      setIsUploading(true);
      setError(null);
      try {
        const parsed = await parseUpload(file);
        setPreview(parsed);
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, 'Не удалось загрузить файл'));
      } finally {
        setIsUploading(false);
      }
    },
    [parseUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files?.[0]) {
        void handleFile(e.dataTransfer.files[0]);
      }
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const confirmUpload = async () => {
    if (!preview || isUploading) return;
    setIsUploading(true);
    setError(null);
    try {
      await addWordsBulk(preview);
      setPreview(null);
      setPasteText('');
      onSuccess();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Не удалось сохранить слова'));
    } finally {
      setIsUploading(false);
    }
  };

  const cancelPreview = () => {
    setPreview(null);
    setPasteText('');
    setError(null);
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!preview ? (
        <>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 transition-colors ${
              dragActive ? 'border-brand-500 bg-brand-50' : 'border-gray-300 bg-gray-50'
            }`}
          >
            <Upload size={24} className="text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Перетащите CSV или TXT файл сюда
            </p>
            <p className="text-xs text-gray-400">или</p>
            <label className="mt-2 cursor-pointer rounded-lg bg-white px-4 py-2 text-sm font-medium text-brand-600 shadow-sm hover:bg-gray-50">
              Выберите файл
              <input
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFile(file);
                  e.target.value = '';
                }}
              />
            </label>
            {isUploading && (
              <div className="mt-2 flex items-center gap-2 text-sm text-brand-600">
                <Loader2 size={14} className="animate-spin" />
                Обработка...
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <FileText size={16} />
              Или вставьте список слов
            </div>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={4}
              placeholder="elma&#10;araba&#10;kitap|книга&#10;masa;стол"
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <button
              onClick={() => void handlePaste()}
              disabled={!pasteText.trim()}
              className="mt-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Разобрать
            </button>
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-brand-200 bg-brand-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-medium text-brand-900">
              Предпросмотр ({preview.length} слов)
            </h3>
            <button onClick={cancelPreview} className="rounded p-1 text-brand-700 hover:bg-brand-100">
              <X size={16} />
            </button>
          </div>
          <div className="max-h-48 overflow-x-auto rounded-lg border border-brand-100 bg-white">
            <table className="w-full min-w-[320px] text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Турецкий</th>
                  <th className="px-3 py-2 text-left font-medium">Русский</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {preview.map((w, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">{w.turkish}</td>
                    <td className="px-3 py-2 text-gray-600">{w.russian || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={cancelPreview}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white"
            >
              Отмена
            </button>
            <button
              onClick={() => void confirmUpload()}
              disabled={isUploading}
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Сохранить слова
            </button>
          </div>
        </div>
      )}
    </div>
  );
}